import { Phone, MapPin, Clock, Mail } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ContactForm from "@/components/ContactFormNetlify";
import AppointmentCalendar from "@/components/AppointmentCalendarNetlify";
import GoogleMap from "@/components/GoogleMap";

const ContactPage = () => {
  return (
    <div className="bg-[#F5F5F0]" data-testid="contact-page">
      {/* Hidden forms for Netlify to detect */}
      <form name="contact" data-netlify="true" hidden>
        <input type="text" name="name" />
        <input type="email" name="email" />
        <input type="tel" name="phone" />
        <textarea name="message"></textarea>
      </form>
      <form name="appointment" data-netlify="true" hidden>
        <input type="text" name="name" />
        <input type="email" name="email" />
        <input type="tel" name="phone" />
        <input type="text" name="preferred_date" />
        <input type="text" name="preferred_time" />
        <textarea name="message"></textarea>
      </form>
      {/* Hero Section */}
      <section className="bg-white section-padding" data-testid="contact-hero">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="heading-playfair heading-xl text-slate-900 mb-6">
              Contactez-nous
            </h1>
            <p className="text-body-lg">
              Nous sommes là pour répondre à vos questions et vous accompagner avec respect.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="section-padding" data-testid="contact-main">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              {/* Phone */}
              <div className="card p-6">
                <div className="flex items-start gap-4">
                  <div className="icon-circle bg-[#F5F5F0]">
                    <Phone className="w-5 h-5 text-slate-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Téléphone</h3>
                    <a 
                      href="tel:450-441-3636" 
                      className="text-[#D4AF37] hover:underline text-lg font-medium"
                      data-testid="contact-phone-link"
                    >
                      450-441-3636
                    </a>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="card p-6">
                <div className="flex items-start gap-4">
                  <div className="icon-circle bg-[#F5F5F0]">
                    <MapPin className="w-5 h-5 text-slate-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Adresse</h3>
                    <address className="not-italic text-slate-600 text-sm leading-relaxed">
                      1501, place de l'Hôtel-de-Ville<br />
                      Saint-Bruno-de-Montarville<br />
                      Québec J3V 5Y6
                    </address>
                  </div>
                </div>
              </div>

              {/* Hours */}
              <div className="card p-6">
                <div className="flex items-start gap-4">
                  <div className="icon-circle bg-[#F5F5F0]">
                    <Clock className="w-5 h-5 text-slate-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Heures d'ouverture</h3>
                    <div className="text-slate-600 text-sm space-y-1">
                      <p>Vendredis</p>
                      <p>Un mercredi sur deux (soirée)</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Call CTA */}
              <a
                href="tel:450-441-3636"
                className="btn-call w-full justify-center text-base py-4"
                data-testid="contact-call-button"
              >
                <Phone className="w-5 h-5" />
                <span>Appeler maintenant</span>
              </a>
            </div>

            {/* Forms */}
            <div className="lg:col-span-2">
              <div className="card p-8">
                <Tabs defaultValue="appointment" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-8 bg-stone-100">
                    <TabsTrigger 
                      value="appointment" 
                      className="data-[state=active]:bg-white data-[state=active]:text-slate-900"
                      data-testid="tab-appointment"
                    >
                      Demande de rendez-vous
                    </TabsTrigger>
                    <TabsTrigger 
                      value="message"
                      className="data-[state=active]:bg-white data-[state=active]:text-slate-900"
                      data-testid="tab-message"
                    >
                      Nous écrire
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="appointment" data-testid="tab-content-appointment">
                    <div className="mb-6">
                      <h2 className="heading-playfair text-xl font-semibold text-slate-900 mb-2">
                        Demandez un rendez-vous
                      </h2>
                      <p className="text-slate-600 text-sm">
                        Sélectionnez une date et nous vous contacterons pour confirmer.
                      </p>
                    </div>
                    <AppointmentCalendar />
                  </TabsContent>
                  
                  <TabsContent value="message" data-testid="tab-content-message">
                    <div className="mb-6">
                      <h2 className="heading-playfair text-xl font-semibold text-slate-900 mb-2">
                        Envoyez-nous un message
                      </h2>
                      <p className="text-slate-600 text-sm">
                        Nous vous répondrons dans les plus brefs délais.
                      </p>
                    </div>
                    <ContactForm />
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-white section-padding" data-testid="contact-map-section">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="heading-playfair heading-md text-slate-900 mb-4">
              Nous trouver
            </h2>
            <p className="text-body max-w-xl mx-auto">
              Situé au cœur de Saint-Bruno-de-Montarville, notre clinique est facilement accessible.
            </p>
          </div>
          <GoogleMap />
        </div>
      </section>

      {/* Final Reassurance */}
      <section className="section-padding" data-testid="contact-reassurance">
        <div className="container-custom">
          <div className="card p-8 md:p-12 max-w-3xl mx-auto text-center">
            <Mail className="w-12 h-12 text-[#D4AF37] mx-auto mb-6" />
            <h2 className="heading-playfair heading-md text-slate-900 mb-4">
              Nous sommes là pour vous
            </h2>
            <p className="text-body-lg">
              Nous sommes là pour répondre à vos questions et vous accompagner avec respect. 
              N'hésitez pas à nous contacter, quelle que soit votre interrogation.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
